import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import axios, { endpoints } from '../utils/axios';
import { getSessionCancellationPreview, cancelSessionWithRefund } from 'src/lib/session-cancellation-api';

import { getPlayerDisplayName } from 'src/utils/get-player-display-name';
export function useSessions(filters = {}) {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [meta, setMeta] = useState({
        page: 1,
        limit: 20,
        total: 0
    });

    // Use ref to track if initial fetch has been done
    const initialFetchDone = useRef(false);
    const memoizedFilters = useMemo(() => filters, [JSON.stringify(filters)]);

    const fetchSessions = useCallback(async (params = {}) => {
        try {
            setLoading(true);
            setError(null);

            let response;

            // Check if we're fetching sessions for a specific facility
            if (memoizedFilters.facility_id) {
                // Use the facility-specific sessions endpoint
                const searchParams = new URLSearchParams();

                // Add other filters to search params (excluding facility_id since it's in the URL)
                Object.entries({ ...memoizedFilters, ...params }).forEach(([key, value]) => {
                    if (key !== 'facility_id' && value !== null && value !== undefined && value !== '' && value !== 'all') {
                        searchParams.append(key, value);
                    }
                });

                // Add default sorting
                if (!searchParams.has('sort')) {
                    searchParams.append('sort', 'start_time:asc');
                }

                const queryString = searchParams.toString();
                const url = `${endpoints.facilities.sessions(memoizedFilters.facility_id)}${queryString ? `?${queryString}` : ''}`;
                response = await axios.get(url);
            }
            // Check if we're fetching sessions for a specific club
            else if (memoizedFilters.club_id) {
                // Use the club-specific sessions endpoint
                const searchParams = new URLSearchParams();

                // Add other filters to search params (excluding club_id since it's in the URL)
                Object.entries({ ...memoizedFilters, ...params }).forEach(([key, value]) => {
                    if (key !== 'club_id' && value !== null && value !== undefined && value !== '' && value !== 'all') {
                        searchParams.append(key, value);
                    }
                });

                // Add default sorting
                if (!searchParams.has('sort')) {
                    searchParams.append('sort', 'start_time:asc');
                }

                const queryString = searchParams.toString();
                const url = `${endpoints.clubs.sessions(memoizedFilters.club_id)}${queryString ? `?${queryString}` : ''}`;
                response = await axios.get(url);
            } else {
                // Build query string for general sessions endpoint
                const searchParams = new URLSearchParams();

                // Add filters to search params
                Object.entries({ ...memoizedFilters, ...params }).forEach(([key, value]) => {
                    if (value !== null && value !== undefined && value !== '' && value !== 'all') {
                        searchParams.append(key, value);
                    }
                });

                response = await axios.get(`${endpoints.sessions.list}?${searchParams.toString()}`);
            }

            console.log(`Fetching sessions with response:`, response.data);

            const data = response.data;

            if (data.status === 'success') {
                // Handle different response structures
                let sessionsData;
                let metaData;

                if (memoizedFilters.facility_id || memoizedFilters.club_id) {
                    // Facility-specific and Club-specific sessions API returns data.items as array
                    sessionsData = data.data.items || [];
                    metaData = {
                        page: data.data.page || 1,
                        limit: data.data.limit || 20,
                        total: data.data.total || 0,
                    };
                } else {
                    // General sessions API also returns data.items
                    sessionsData = data.data.items || [];
                    metaData = {
                        page: data.data.page || 1,
                        limit: data.data.limit || 20,
                        total: data.data.total || 0,
                    };
                }

                setSessions(sessionsData);
                setMeta(metaData);
            } else {
                throw new Error(data.message || 'Failed to fetch sessions');
            }
        } catch (err) {
            setError(err.message);
            setSessions([]);
        } finally {
            setLoading(false);
        }
    }, [memoizedFilters]);

    useEffect(() => {
        // Only fetch once on mount, then only when filters actually change
        if (!initialFetchDone.current) {
            fetchSessions();
            initialFetchDone.current = true;
        }
    }, []);

    // Separate effect for filter changes
    useEffect(() => {
        if (initialFetchDone.current) {
            fetchSessions();
        }
    }, [memoizedFilters]);

    const getSessionById = useCallback((id) => {
        return sessions.find(session => session.id === id);
    }, [sessions]);

    const createSession = useCallback(async (sessionData) => {
        try {
            const response = await axios.post(endpoints.sessions.create, sessionData);

            const data = response.data;

            if (data.status === 'success') {
                // Refresh the sessions list
                await fetchSessions();
                return data.data;
            } else {
                throw new Error(data.message || 'Failed to create session');
            }
        } catch (err) {
            throw new Error(err.message || 'Failed to create session');
        }
    }, [fetchSessions]);

    const updateSession = useCallback(async (id, sessionData) => {
        try {
            const response = await axios.put(endpoints.sessions.update(id), sessionData);

            const data = response.data;

            if (data.status === 'success') {
                // Refresh the sessions list
                await fetchSessions();
                return data.data;
            } else {
                throw new Error(data.message || 'Failed to update session');
            }
        } catch (err) {
            throw new Error(err.message || 'Failed to update session');
        }
    }, [fetchSessions]);

    const deleteSession = useCallback(async (id) => {
        try {
            const response = await axios.delete(endpoints.sessions.delete(id));

            const data = response.data;

            if (data.status === 'success') {
                // Refresh the sessions list
                await fetchSessions();
                return true;
            } else {
                throw new Error(data.message || 'Failed to delete session');
            }
        } catch (err) {
            throw new Error(err.message || 'Failed to delete session');
        }
    }, [fetchSessions]);

    const refreshSessions = useCallback(() => {
        return fetchSessions();
    }, [fetchSessions]);

    /** Preview of a facility cancellation (registered/paid players, total refund). Informational only. */
    const getCancellationPreview = useCallback(
        (id) => getSessionCancellationPreview(id),
        []
    );

    /**
     * Cancel a session and refund every eligible paid player to their wallet.
     * The backend computes every amount from payment records; the payload only
     * carries the reason. Idempotent: a second call returns already_cancelled.
     */
    const cancelSession = useCallback(async (id, payload) => {
        const result = await cancelSessionWithRefund(id, payload);
        await fetchSessions();
        return result;
    }, [fetchSessions]);

    const approvePlayerRequest = useCallback(async (sessionId, userId) => {
        try {
            const response = await axios.post(endpoints.sessions.approveRequest(sessionId), {
                user_id: userId
            });

            const data = response.data;

            if (data.status === 'success') {
                return data.data;
            } else {
                throw new Error(data.message || 'Failed to approve player request');
            }
        } catch (err) {
            throw new Error(err.message || 'Failed to approve player request');
        }
    }, []);

    const rejectPlayerRequest = useCallback(async (sessionId, userId) => {
        try {
            const response = await axios.post(endpoints.sessions.rejectRequest(sessionId), {
                user_id: userId
            });

            const data = response.data;

            if (data.status === 'success') {
                return data.data;
            } else {
                throw new Error(data.message || 'Failed to reject player request');
            }
        } catch (err) {
            throw new Error(err.message || 'Failed to reject player request');
        }
    }, []);

    const updatePlayerStatus = useCallback(async (sessionId, userId, status) => {
        try {
            const response = await axios.post(endpoints.sessions.updatePlayerStatus(sessionId), {
                user_id: userId,
                status: status
            });

            const data = response.data;

            if (data.status === 'success') {
                return data.data;
            } else {
                throw new Error(data.message || 'Failed to update player status');
            }
        } catch (err) {
            throw new Error(err.message || 'Failed to update player status');
        }
    }, []);

    const updatePlayerPayment = useCallback(async (sessionId, userId, paymentStatus) => {
        try {
            const response = await axios.post(endpoints.sessions.updatePlayerPayment(sessionId), {
                user_id: userId,
                payment_status: paymentStatus
            });

            const data = response.data;

            if (data.status === 'success') {
                return data.data;
            } else {
                throw new Error(data.message || 'Failed to update player payment status');
            }
        } catch (err) {
            throw new Error(err.message || 'Failed to update player payment status');
        }
    }, []);

    /**
     * Add ONE player to a session. Two distinct operations share this endpoint,
     * and the backend branches on which identifier is present:
     *   { userId }  -> attach an EXISTING account (no user is created)
     *   { email }   -> look the account up by email, creating it if new
     * Never send both: the DTO rejects an ambiguous body.
     *
     * For picking several existing players at once, prefer addPlayersToSession.
     */
    const addPlayerToSession = useCallback(async (sessionId, playerData, paymentMethod = 'offline') => {
        const requestBody = {
            payment_method: paymentMethod
        };

        if (playerData.userId) {
            requestBody.user_id = playerData.userId;
        } else if (playerData.email) {
            requestBody.email = playerData.email;
            // Only when the caller actually has one. This used to be assigned
            // unconditionally from a field no form produces, so every new-player
            // request carried `name: undefined`.
            if (playerData.name) requestBody.name = playerData.name;
        }

        // Profile fields — shared by both paths. skill_level belongs here too:
        // it used to sit inside the email branch, so picking a skill level while
        // adding an EXISTING player silently did nothing.
        if (playerData.first_name) requestBody.first_name = playerData.first_name;
        if (playerData.last_name) requestBody.last_name = playerData.last_name;
        if (playerData.nick_name) requestBody.nick_name = playerData.nick_name;
        if (playerData.display_as) requestBody.display_as = playerData.display_as;
        if (playerData.skill_level) requestBody.skill_level = playerData.skill_level;
        if (playerData.mark_as_paid !== undefined) requestBody.mark_as_paid = playerData.mark_as_paid;
        // Open Play walk-ins: seat the player straight into the Ready pool.
        if (playerData.mark_ready !== undefined) requestBody.mark_ready = playerData.mark_ready;

        const response = await axios.post(endpoints.sessions.addPlayer(sessionId), requestBody);

        const data = response.data;

        if (data.status === 'success') {
            return data.data;
        }
        throw new Error(data.message || 'Failed to add player to session');
        // No catch: the axios interceptor already rejects with an Error carrying
        // .status / .code / .errors / .data. Re-wrapping it here read
        // `err.response.data.message`, which this interceptor never produces, so
        // it always collapsed to the generic text AND destroyed the fields the
        // UI needs to tell a 403 from a 409 from a validation error.
    }, []);

    // Players who can be added to a session, searched/filtered/paged server-side.
    // Errors are rethrown as-is: the axios interceptor already attaches
    // `status`, `code` and `data`, and the picker needs `status` to tell a 403
    // apart from an empty result.
    const getAvailablePlayers = useCallback(async (sessionId, params = {}) => {
        const query = Object.fromEntries(
            Object.entries(params).filter(
                ([, value]) => value !== undefined && value !== null && value !== '' && value !== 'undefined'
            )
        );
        const response = await axios.get(endpoints.sessions.availablePlayers(sessionId), { params: query });
        const { data } = response;
        if (data.status !== 'success') {
            throw new Error(data.message || 'Failed to load players');
        }
        return data.data;
    }, []);

    // Bulk add. A 409 SESSION_FULL rejection carries the capacity numbers on
    // `err.data` (see the axios interceptor) so the picker can show them.
    const addPlayersToSession = useCallback(async (sessionId, { user_ids, payment_method = 'cash', mark_as_paid = true, mark_ready }) => {
        const response = await axios.post(endpoints.sessions.addPlayers(sessionId), {
            user_ids,
            payment_method,
            mark_as_paid,
            // Only when the caller has the control (Open Play); Session Details omits it.
            ...(mark_ready !== undefined ? { mark_ready } : {}),
        });
        const { data } = response;
        if (data.status !== 'success') {
            throw new Error(data.message || 'Failed to add players to session');
        }
        return data.data;
    }, []);

    return {
        sessions,
        loading,
        error,
        meta,
        getSessionById,
        createSession,
        updateSession,
        deleteSession,
        refreshSessions,
        fetchSessions,
        getCancellationPreview,
        cancelSession,
        approvePlayerRequest,
        rejectPlayerRequest,
        updatePlayerStatus,
        updatePlayerPayment,
        addPlayerToSession,
        getAvailablePlayers,
        addPlayersToSession,
    };
}



export function useSession(id) {
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchSession = useCallback(async () => {
        if (!id) {
            setSession(null);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const response = await axios.get(endpoints.sessions.get(id));
            const data = response.data;

            if (data.status === 'success') {
                setSession(data.data);
            } else {
                throw new Error(data.message || 'Failed to fetch session');
            }
        } catch (err) {
            setError(err.message);
            // A failed REFETCH of the session already on screen keeps the last
            // good copy: dropping it blanked every screen built on it (the Open
            // Play board lost its courts and rotation panel on one network blip).
            setSession((current) => (current?.id === id ? current : null));
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchSession();
    }, [fetchSession]);

    return {
        session,
        loading,
        error,
        refetch: fetchSession,
    };
}
